import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CreditCard, Crown, Loader2, RefreshCw, Sparkles, Users } from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-black/60 p-4" data-testid={`plans-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

export default function PlansView() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const s = await crmApi.plansSummary();
      setSummary(s);
    } catch (exc) {
      toast.error(exc.message || "Failed to load plans summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const s = await crmApi.plansSummary();
        if (active) setSummary(s);
      } catch (exc) {
        if (active) toast.error(exc.message || "Failed to load plans summary");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const provision = async (slug) => {
    setBusy(true);
    try {
      await crmApi.provisionTier(slug);
      toast.success(`Provisioned tier ${slug} in Stripe (test mode)`);
      reload();
    } catch (exc) {
      toast.error(exc.message || `Provision ${slug} failed`);
    } finally {
      setBusy(false);
    }
  };

  if (loading || !summary) {
    return (
      <div className="text-sm text-zinc-400 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading plans…
      </div>
    );
  }

  const stripe = summary.stripe || {};
  const stripeReady = stripe.configured;

  return (
    <div className="space-y-6" data-testid="owner-plans">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Maintenance Plans</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Recurring revenue engine. Members, MRR, Stripe state, and Duo Pest Defense dispatch live here.
            Stripe currently runs in <span className="text-amber-300">{stripe.mode || "unconfigured"} mode</span>.
          </p>
        </div>
        <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900" onClick={reload} data-testid="plans-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="plans-kpi-row">
        <Stat label="Active members" value={summary.members_active} accent="text-amber-300" />
        <Stat label="Total members" value={summary.members_total} />
        <Stat label="MRR" value={`$${(summary.mrr_usd || 0).toLocaleString()}`} accent="text-emerald-300" />
        <Stat label="Upcoming dispatches" value={summary.upcoming_dispatches} />
      </div>

      <Card className="bg-black/40 border-zinc-800" data-testid="plans-tiers-card">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm text-white flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-300" /> Subscription tiers (sourced from Sales Knowledge Library)
          </CardTitle>
          <Badge variant="outline" className={stripeReady ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}>
            stripe: {stripe.mode || "unconfigured"}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-3">
          {!stripeReady && (
            <div className="text-xs text-amber-200/90 bg-amber-500/5 border border-amber-500/20 rounded-md p-3">
              {stripe.reason || "Stripe is not configured. Add STRIPE_SECRET_KEY (sk_test_…) to /app/backend/.env to enable checkout in test mode."}
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(summary.tiers || []).map((t) => (
              <div key={t.slug} className="rounded-lg border border-zinc-800 bg-black/60 p-4 space-y-2" data-testid={`tier-card-${t.slug}`}>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-white">{t.display_name}</div>
                    <div className="text-[11px] uppercase tracking-wide text-zinc-500">slug · {t.slug}</div>
                  </div>
                  {t.includes_pest_control && (
                    <Badge variant="outline" className="border-amber-500/40 text-amber-300">+ pest</Badge>
                  )}
                </div>
                <div className="text-xs text-zinc-300">
                  <div>${(t.monthly_price_usd || 0).toLocaleString()} / mo</div>
                  {t.annual_price_usd ? <div>${(t.annual_price_usd || 0).toLocaleString()} / yr</div> : null}
                </div>
                <div className="text-[11px] text-zinc-500 break-all">
                  {t.stripe_product_id ? `prod_id: ${t.stripe_product_id.slice(0, 14)}…` : "not provisioned in Stripe yet"}
                </div>
                <div className="flex justify-end">
                  {!t.stripe_product_id && (
                    <Button size="sm" disabled={!stripeReady || busy} onClick={() => provision(t.slug)}
                      className="bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-60"
                      data-testid={`provision-tier-${t.slug}`}>
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Provision</>}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-2 gap-3">
        <Card className="bg-black/40 border-zinc-800">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-white flex items-center gap-2"><Users className="h-4 w-4 text-amber-300" /> Members</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-zinc-300">Manage subscribers, generate magic links for the member portal, and adjust statuses.</p>
            <Button asChild size="sm" variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900" data-testid="plans-go-members">
              <Link to="/admin/owner/members">Open Members</Link>
            </Button>
          </CardContent>
        </Card>
        <Card className="bg-black/40 border-zinc-800">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-white flex items-center gap-2"><CreditCard className="h-4 w-4 text-amber-300" /> Dispatches & Attach</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-zinc-300">Run bi-monthly Duo Pest Defense visits and nurture post-job plan attach opportunities.</p>
            <div className="flex flex-wrap gap-2">
              <Button asChild size="sm" variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900" data-testid="plans-go-dispatches">
                <Link to="/admin/owner/dispatches">Dispatches</Link>
              </Button>
              <Button asChild size="sm" variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900" data-testid="plans-go-attach">
                <Link to="/admin/owner/attach">Attach flywheel</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
